import React, { useMemo, useState } from 'react';
import { Grid, Header3Text, ModalWindow, SwitchDialogWindows } from 'ui-toolkit/components';
import {
  defaultPolicyPremiumDetailsData,
  IUpdatePolicyPremiumDetailsRequest,
} from 'types/order/title/IPolicyPremiumDetailsResponse';
import { ChangeHoldOpenFormModal } from './ChangeHoldOpenFormModal';
import { HoldOpenNavigationItems } from './HoldOpenNavigationItems';
import { FontAwesomeIconColor } from '../../../../../shared/FontAweomeIconColor/FontAwesomeIconColor';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { nbsp } from 'utilities/administration/magicStrings';
import { convertToError } from '../../../../../../API/BaseApi';
import { Show } from '../../../../../shared/Show';
import {
  useGetPolicyPremiumDetailsQuery,
  useUpdatePolicyPremiumDetailsMutation,
} from '../../../../../../API/order/title/PolicyPremiumDetailsAPI';
import { checkForMutationSuccess } from '../../../../../../API/checkForMutationSuccess';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../../../types/shared/UUID';

export const HoldOpenDisplay: React.FC = () => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const { data: { holdOpenAvailable, holdOpen } = defaultPolicyPremiumDetailsData } =
    useGetPolicyPremiumDetailsQuery(orderId);

  const [showChangeHoldOpenFormModal, setShowChangeHoldOpenFormModal] = useState<boolean>(false);
  const [
    updatePolicyPremiumDetailsMutation,
    { isSuccess: isUpdateSuccess, isLoading: isUpdateLoading, isError: isUpdateError, error: updateError },
  ] = useUpdatePolicyPremiumDetailsMutation();

  const onUpdateTitlePolicyPremiumDetails = async (updateRequest: IUpdatePolicyPremiumDetailsRequest) => {
    const result = await updatePolicyPremiumDetailsMutation({ orderId, updateRequest });
    checkForMutationSuccess(result, 'updated policy premium details');
  };

  const handleCancel = () => {
    setShowChangeHoldOpenFormModal(false);
  };

  const errorMessage = useMemo(() => convertToError(updateError)?.message, [updateError]);

  if (!holdOpenAvailable) {
    return null;
  }

  return (
    <Grid data-test-id={'hold-open-display'}>
      <HoldOpenNavigationItems holdOpen={holdOpen} setShowChangeHoldOpenFormModal={setShowChangeHoldOpenFormModal} />
      <Show when={showChangeHoldOpenFormModal}>
        <SwitchDialogWindows
          isCompleted={isUpdateSuccess}
          isError={isUpdateError}
          errorMessage={errorMessage}
          isLoading={isUpdateLoading}
          onCloseClick={handleCancel}
          successMessage={'Hold Open status changed'}
          loadingMessage={'Submitting change'}
        >
          <ModalWindow onClose={handleCancel} title={<ModalWindowTitle holdOpen={holdOpen} />}>
            <ChangeHoldOpenFormModal
              handleCancel={handleCancel}
              handleSubmit={onUpdateTitlePolicyPremiumDetails}
              holdOpen={holdOpenAvailable && !!holdOpen}
              holdOpenAvailable={holdOpenAvailable}
            />
          </ModalWindow>
        </SwitchDialogWindows>
      </Show>
    </Grid>
  );
};

const ModalWindowTitle: React.FC<{ holdOpen?: boolean }> = ({ holdOpen }) => (
  <Header3Text>
    <FontAwesomeIconColor icon={faTriangleExclamation} intent={'danger'} />
    {`${nbsp}${holdOpen ? 'Are you sure?' : 'Issue a Hold Open'}`}
  </Header3Text>
);
